import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
  index?: number;
  showQuickAdd?: boolean;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

function getImageUrl(path: string) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
}

const ProductCard = ({ product, index = 0, showQuickAdd = true }: ProductCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hovered, setHovered] = useState(false);

  const hasMultipleImages = product.images && product.images.length > 1;
  const primaryImage = product.images?.[0] || '';
  const secondaryImage = hasMultipleImages ? product.images[1] : '';
  const hasSale = product.salePrice && product.salePrice < product.price;
  const discount = hasSale
    ? Math.round(((product.price - product.salePrice!) / product.price) * 100)
    : 0;

  return (
    <div
      className="product-card group reveal"
      style={{ animationDelay: `${index * 80}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link
        to={`/product/${product._id}`}
        className="block"
        aria-label={`View ${product.name} — ${hasSale ? product.salePrice : product.price} DA`}
      >
        {/* Image */}
        <div className="product-card-image">
          {!imageLoaded && (
            <div className="absolute inset-0 loading-skeleton" />
          )}

          <img
            src={getImageUrl(hovered && secondaryImage ? secondaryImage : primaryImage)}
            alt={product.name}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            className={`transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          />

          {/* Badges */}
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
            {hasSale && (
              <span className="badge-sale">-{discount}%</span>
            )}
            {!hasSale && product.createdAt && isNew(product.createdAt) && (
              <span className="badge-new">New</span>
            )}
          </div>

          {/* Hover overlay with Quick View */}
          {showQuickAdd && (
            <div className="product-card-overlay">
              <span className="btn-sm btn-primary bg-offwhite/90 text-offblack backdrop-blur-sm hover:bg-offwhite">
                Quick View
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="product-card-info">
          {product.brand && (
            <p className="text-[0.65rem] uppercase tracking-[0.15em] text-warmgray-400 font-medium">
              {product.brand}
            </p>
          )}
          <h3 className="product-card-name">{product.name}</h3>
          <div className="flex items-center gap-2">
            {hasSale ? (
              <>
                <span className="product-card-price product-card-price--sale font-medium">
                  {product.salePrice?.toLocaleString()} DA
                </span>
                <span className="text-xs text-warmgray-400 line-through">
                  {product.price.toLocaleString()} DA
                </span>
              </>
            ) : (
              <span className="product-card-price">
                {product.price.toLocaleString()} DA
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

function isNew(createdAt: string): boolean {
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays <= 14;
}

export default ProductCard;
